import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, Send, Loader2 } from "lucide-react";
import API from "@/lib/api";

const presets = [
  "Plan a 2-day Puri beach & temple itinerary",
  "Best scooter routes inside Puri",
  "Car fare for Puri to Chilika Lake day trip",
];

export default function AiPlanner({ open, setOpen }) {
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Namaste! I'm your Random Rider trip planner. Ask me about Puri itineraries, rentals or fares anywhere in Odisha." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef(localStorage.getItem("cr_ai_session") || null);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", text: msg }, { role: "assistant", text: "" }]);
    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg, session_id: sessionRef.current }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          try {
            const data = JSON.parse(line.slice(5).trim());
            if (data.session_id) {
              sessionRef.current = data.session_id;
              localStorage.setItem("cr_ai_session", data.session_id);
            }
            if (data.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", text: copy[copy.length - 1].text + data.delta };
                return copy;
              });
            }
          } catch { /* partial json */ }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: "Connection hiccup — please try again." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <AnimatePresence>
        {!open && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            onClick={() => setOpen(true)}
            data-testid="ai-planner-open-button"
            className="fixed bottom-6 left-6 z-[80] flex items-center gap-2 bg-ink text-sand font-semibold text-sm pl-4 pr-5 py-3.5 rounded-full shadow-xl hover:bg-terra transition-colors duration-300"
          >
            <Sparkles size={17} className="text-gold" /> AI Trip Planner
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-6 left-4 right-4 sm:right-auto sm:left-6 z-[85] sm:w-[400px] h-[70vh] max-h-[560px] bg-white rounded-3xl shadow-2xl border border-ink/10 flex flex-col overflow-hidden"
            data-testid="ai-planner-panel"
          >
            <div className="bg-ink text-sand px-5 py-4 flex items-center gap-3">
              <span className="w-9 h-9 rounded-full bg-terra flex items-center justify-center"><Sparkles size={16} /></span>
              <div className="flex-1">
                <p className="font-serif font-bold leading-tight">Random Rider AI</p>
                <p className="text-[11px] text-sand/60">Your Puri & Odisha trip expert</p>
              </div>
              <button onClick={() => setOpen(false)} data-testid="ai-planner-close-button" className="text-sand/60 hover:text-sand"><X size={20} /></button>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-sand/50" data-testid="ai-planner-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user" ? "bg-terra text-sand rounded-br-md" : "bg-white border border-ink/10 text-ink rounded-bl-md"
                  }`}>
                    {m.text || <Loader2 size={15} className="animate-spin text-terra" />}
                  </div>
                </div>
              ))}
            </div>

            {messages.length <= 1 && (
              <div className="px-4 pb-2 flex flex-wrap gap-2 bg-sand/50">
                {presets.map((p) => (
                  <button key={p} onClick={() => send(p)} data-testid={`ai-preset-${p.slice(0, 18).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="text-[11px] bg-white border border-terra/30 text-terra font-medium px-3 py-1.5 rounded-full hover:bg-terra hover:text-sand transition-colors">
                    {p}
                  </button>
                ))}
              </div>
            )}

            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-ink/10 flex gap-2 bg-white">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about Puri trips, rentals, fares..."
                data-testid="ai-planner-input"
                className="flex-1 bg-sand rounded-full px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-terra/50"
              />
              <button type="submit" disabled={busy} data-testid="ai-planner-send-button"
                className="w-10 h-10 rounded-full bg-terra text-sand flex items-center justify-center hover:bg-ink transition-colors disabled:opacity-50">
                <Send size={16} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
